import React, { useState, useEffect, useRef, useCallback } from "react";
import { MapPin, Navigation, AlertTriangle, RefreshCw, ShieldCheck } from "lucide-react";
import TspApiService from "../../api/tspApiService";

const PING_INTERVAL_MS = 30000; // 30 seconds interval as chosen by user

export default function TspLocationEnforcer() {
  const [status, setStatus] = useState("checking"); // 'checking' | 'active' | 'denied' | 'unavailable' | 'unsupported'
  const [errorMsg, setErrorMsg] = useState("");
  const [lastFix, setLastFix] = useState(null);
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  const latestPositionRef = useRef(null);

  // Send coordinates ping to backend
  const sendPing = useCallback(async (coords) => {
    if (!coords || !coords.latitude || !coords.longitude) return;
    try {
      let batteryLevel = null;
      if (typeof navigator !== "undefined" && typeof navigator.getBattery === "function") {
        try {
          const battery = await navigator.getBattery();
          batteryLevel = battery.level;
        } catch (_) {}
      }

      if (typeof TspApiService?.userLocations?.ping === "function") {
        await TspApiService.userLocations.ping({
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: coords.accuracy || 10,
          speed: coords.speed != null ? coords.speed : null,
          heading: coords.heading != null ? coords.heading : null,
          battery_level: batteryLevel,
        });
      }
    } catch (err) {
      // Background ping failure (e.g. temporary network blip) is handled silently
      console.warn("Location ping failed:", err?.message || err);
    }
  }, []);

  const handlePositionSuccess = useCallback((position) => {
    const coords = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      speed: position.coords.speed,
      heading: position.coords.heading,
      timestamp: position.timestamp,
    };
    latestPositionRef.current = coords;
    setLastFix(coords);
    setStatus("active");
    setErrorMsg("");
    sendPing(coords);
  }, [sendPing]);

  const handlePositionError = useCallback((error) => {
    if (error.code === 1) { // PERMISSION_DENIED
      setStatus("denied");
      setErrorMsg("Location permission was denied. Please allow location access in your browser site settings.");
    } else if (error.code === 2) { // POSITION_UNAVAILABLE
      setStatus("unavailable");
      setErrorMsg("Device GPS / location is turned off or unavailable. Please enable GPS in device settings.");
    } else if (error.code === 3) { // TIMEOUT
      // Don't show hard block on single timeout if we already have an active fix
      if (!latestPositionRef.current) {
        setStatus("unavailable");
        setErrorMsg("Location request timed out. Please check your GPS signal and retry.");
      }
    } else {
      setStatus("unavailable");
      setErrorMsg(error.message || "Could not retrieve GPS position.");
    }
  }, []);

  const startTracking = useCallback(() => {
    if (!navigator || !navigator.geolocation || typeof navigator.geolocation.getCurrentPosition !== "function") {
      setStatus("unsupported");
      setErrorMsg("Your browser does not support Geolocation. Please use Chrome, Safari or Edge.");
      return;
    }

    setStatus("checking");
    setErrorMsg("");

    // 1. Initial quick fix request to prompt browser permission dialog
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        handlePositionSuccess(pos);

        // 2. Continuous watchPosition
        if (watchIdRef.current !== null) {
          navigator.geolocation.clearWatch(watchIdRef.current);
        }
        watchIdRef.current = navigator.geolocation.watchPosition(
          handlePositionSuccess,
          handlePositionError,
          { enableHighAccuracy: true, timeout: 20000, maximumAge: 10000 }
        );
      },
      handlePositionError,
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, [handlePositionSuccess, handlePositionError]);

  useEffect(() => {
    startTracking();

    // Background 30-second heartbeat ping
    intervalRef.current = setInterval(() => {
      if (latestPositionRef.current) {
        sendPing(latestPositionRef.current);
      }
    }, PING_INTERVAL_MS);

    // Refresh position immediately when tab regains focus or becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        if (typeof navigator?.geolocation?.getCurrentPosition === "function") {
          navigator.geolocation.getCurrentPosition(handlePositionSuccess, () => {}, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 5000,
          });
        }
      }
    };

    // Resend when reconnecting to internet
    const handleOnline = () => {
      if (latestPositionRef.current) {
        sendPing(latestPositionRef.current);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);

    return () => {
      if (watchIdRef.current !== null && typeof navigator?.geolocation?.clearWatch === "function") {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
    };
  }, [startTracking, sendPing, handlePositionSuccess]);

  // If active, render nothing (runs silently in the background)
  if (status === "active") {
    return null;
  }

  // If status is checking, render null to allow normal load until an error or prompt triggers
  if (status === "checking") {
    return null;
  }

  // Mandatory modal: user CANNOT close or bypass until GPS permission is granted
  return (
    <div className="tsp-gps-mandatory-backdrop" role="alertdialog" aria-modal="true" aria-labelledby="tsp-gps-modal-title">
      <div className="tsp-gps-mandatory-modal">
        <div className="tsp-gps-modal-badge">
          <ShieldCheck size={14} /> Mandatory Verification
        </div>

        <div className="tsp-gps-modal-icon-wrap">
          <div className="tsp-gps-pulse-ring" />
          <MapPin size={38} className="tsp-gps-pin-icon" />
        </div>

        <h2 id="tsp-gps-modal-title">Device GPS Location Required</h2>

        <p className="tsp-gps-modal-desc">
          To access the TSP portal, your device’s live GPS location must be turned on and permitted.
          This ensures live field coordination, team tracking, and security.
        </p>

        {errorMsg && (
          <div className="tsp-gps-modal-alert">
            <AlertTriangle size={16} />
            <span>{errorMsg}</span>
          </div>
        )}

        <div className="tsp-gps-modal-guide">
          <strong>How to enable location:</strong>
          <ol>
            <li>Turn <strong>ON</strong> your device GPS / Location in Quick Settings.</li>
            <li>Click the <strong>Permissions / Lock (🔒) icon</strong> in your browser address bar.</li>
            <li>Select <strong>Location &rarr; Allow</strong>, then click the button below.</li>
          </ol>
        </div>

        <button
          type="button"
          className="tsp-btn tsp-btn-primary tsp-btn-lg tsp-btn-block tsp-gps-retry-btn"
          onClick={startTracking}
        >
          <Navigation size={18} /> Turn On GPS / Retry Access
        </button>
      </div>
    </div>
  );
}
